import React, { useState, useEffect } from 'react';
import {
  Plus,
  Check,
  Clock,
  Circle,
  Trash2,
  Edit2,
  X,
  AlertCircle,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import apiService from './services/api';
import { Panel, LoadingSpinner } from './components';

function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee: '',
    priority: 'medium',
    due_date: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, assigneesRes] = await Promise.all([
        apiService.getTeamTasks(),
        apiService.get('/amn/team-tasks/assignees/')
      ]);
      setTasks(tasksRes.results || tasksRes || []);
      setTeamMembers(assigneesRes || []);
    } catch (err) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', assignee: '', priority: 'medium', due_date: '' });
    setEditingTask(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    try {
      const payload = {
        ...formData,
        assignee: formData.assignee || null,
        due_date: formData.due_date || null
      };
      if (editingTask) {
        await apiService.updateTeamTask(editingTask.id, payload);
      } else {
        await apiService.createTeamTask(payload);
      }
      resetForm();
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save task');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await apiService.updateTeamTask(task.id, { status: newStatus });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await apiService.deleteTeamTask(task.id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (task) => {
    setFormData({
      title: task.title,
      description: task.description || '',
      assignee: task.assignee || '',
      priority: task.priority,
      due_date: task.due_date || ''
    });
    setEditingTask(task);
    setShowForm(true);
  };

  const filteredTasks = tasks.filter(t => {
    if (statusFilter === 'active') return t.status !== 'done';
    if (statusFilter === 'done') return t.status === 'done';
    return true;
  });

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const statusOrder = { in_progress: 0, todo: 1, done: 2 };
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.status !== b.status) return statusOrder[a.status] - statusOrder[b.status];
    if (a.priority !== b.priority) return priorityOrder[a.priority] - priorityOrder[b.priority];
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const priorityColor = (p) => {
    if (p === 'high') return 'bg-red-100 text-red-800';
    if (p === 'medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-600';
  };

  const statusIcon = (s) => {
    if (s === 'done') return <Check className="w-4 h-4 text-green-600" />;
    if (s === 'in_progress') return <Clock className="w-4 h-4 text-blue-600" />;
    return <Circle className="w-4 h-4 text-gray-400" />;
  };

  const counts = {
    active: tasks.filter(t => t.status !== 'done').length,
    done: tasks.filter(t => t.status === 'done').length,
    all: tasks.length
  };

  if (loading) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-semibold text-gray-900">TASKS</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center px-3 py-1.5 text-xs bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          New Task
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-800 text-xs rounded flex items-center">
          <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex border-b border-gray-200 mb-3">
        {[
          { key: 'active', label: `Active (${counts.active})` },
          { key: 'done', label: `Done (${counts.done})` },
          { key: 'all', label: `All (${counts.all})` }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${statusFilter === tab.key ? 'border-red-800 text-red-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* New/Edit Task Form */}
      {showForm && (
        <Panel className="mb-3 p-3">
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700">{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Task title"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              autoFocus
              required
            />
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Assignee</label>
                <select
                  value={formData.assignee}
                  onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                />
              </div>
            </div>
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={resetForm}
                className="px-3 py-1 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1 text-xs text-white bg-red-800 border border-red-900 rounded hover:bg-red-900"
              >
                {editingTask ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Panel>
      )}

      {/* Task List */}
      <Panel className="p-0">
        {sortedTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Circle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{statusFilter === 'done' ? 'No completed tasks yet' : 'No tasks yet'}</p>
            {statusFilter !== 'done' && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="mt-2 text-xs text-red-800 hover:underline"
              >
                Create your first task
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sortedTasks.map((task) => (
              <div key={task.id} className={`flex items-start gap-2 px-3 py-2.5 hover:bg-gray-50 group ${task.status === 'done' ? 'opacity-60' : ''}`}>
                {/* Status toggle */}
                <button
                  onClick={() => handleStatusChange(task, task.status === 'done' ? 'todo' : task.status === 'todo' ? 'in_progress' : 'done')}
                  className="mt-0.5 flex-shrink-0"
                  title={task.status === 'todo' ? 'Start' : task.status === 'in_progress' ? 'Complete' : 'Reopen'}
                >
                  {statusIcon(task.status)}
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      {task.title}
                    </span>
                    <span className={`inline-flex px-1.5 py-0 rounded text-[10px] font-medium ${priorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                  {task.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{task.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                    {task.assignee_name && <span>{task.assignee_name}</span>}
                    {task.due_date && (
                      <span className={new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-500 font-medium' : ''}>
                        Due {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    {task.created_by_name && <span>by {task.created_by_name}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button onClick={() => startEdit(task)} className="text-gray-400 hover:text-gray-600" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(task)} className="text-gray-400 hover:text-red-600" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="pb-16" />
    </div>
  );
}

export default TasksPage;
